import { KeyMap, KIO, KK, OutputMode, Record } from '@hid/define/define'
import { DeviceMapKey } from './device'
import { DVKFromVK, HidFromKK, HIDFromVk } from '@hid/define/define_fn'
import defineType from '@hid/define/define_type'
import { deviceManager } from '@hid/device/device_manager'
import { defaultVK } from '@hid/define/define_keys'
import { MagnetKeyType } from '@libs/types/enum'

class Jingtaiv2 implements DeviceMapKey {
  encodeKey(_params: KIO): KeyMap {
    return [0, 0, 0, 0]
  }

  setKey(params: KIO): jingtaiV2SetKey {
    const res: jingtaiV2SetKey = {
      layer: params.Input.WithFN ? 1 : 0,
      row: this.getRow(params.Input.KK),
      col: this.getCol(params.Input.KK),
      keycode: 0,
    }

    if (params.Output.Mode == OutputMode.Macro) {
      res.keycode = 62720 + params.Output.DataIndex
      return res
    }

    if (params.Output.Mode == OutputMode.Key && params.Output.VK == defaultVK) {
      if (res.layer == 0x01) {
        res.keycode = 1 // 1是穿透键
      } else {
        res.keycode = HidFromKK(params.Input.KK, deviceManager.deviceID)
      }
      return res
    }

    if (defineType.isMedia(params.Output.VK) || defineType.isMouse(params.Output.VK)) {
      const dvk = DVKFromVK(params.Output.VK, deviceManager.deviceID)
      res.keycode = dvk
      return res
    }

    const hid = HIDFromVk(params.Output.VK)
    if (hid == undefined) {
      throw new Error('hid is undefined')
    }
    res.keycode = hid
    return res
  }

  getRow(kk: KK) {
    return (kk >> 8) & 0xff
  }

  getCol(kk: KK) {
    return kk & 0xff
  }

  macroData(macroID: number, records: Record[]) {
    const sendTimes = Math.ceil(records.length / 15)
    const res: jingtaiV2Macro[] = []

    for (let i = 0; i < sendTimes; i++) {
      let actions: jingtaiV2MacroAction[] = []
      const start = i * 15
      const end = i < sendTimes - 1 ? (i + 1) * 15 : records.length
      const data = records.slice(start, end)
      data.forEach((r) => {
        const hid = HIDFromVk(r.Key)
        if (hid == undefined) {
          throw new Error(`${r.Key} is not a valid key`)
        }

        actions.push({
          status: r.Press ? 1 : 0,
          delay: r.Delay + 1,
          keyCode: hid,
        })
      })

      const params: jingtaiV2Macro = {
        macroId: macroID,
        offset: i,
        actions: actions,
      }
      res.push(params)
    }
    return res
  }

  macroMode(macroID: number, item: KIO, records: Record[]) {
    let mode = item.Output.Extension[0]
    let times = 0
    if (mode == 0) {
      mode = 1
    } else if (mode == 1) {
      times = item.Output.Extension[1]
    } else if (mode == 3) {
      mode = 4
    }
    if (mode > 1) {
      times = 0xffff
    }
    const macroMode: jingtaiV2MacroMode = {
      macroId: macroID,
      mode: mode,
      valid: true,
      actNum: records.length,
      repNum: times,
    }
    return macroMode
  }
}

export const jingtaiV2Data = new Jingtaiv2()

export interface jingtaiV2BaseInfo {
  subType: number
  boardId: number
  appVersion: string
  pcbVersion: string
  runModeVersion: number
  sn: string
  timestamp: string
  type: number
}

export interface jingtaiV2SetKey {
  layer: number
  row: number
  col: number
  keycode: number
}

export interface jingtaiV2PerformanceInfo {
  mode: number
  normalPress: number
  normalRelease: number
  rtFirstTouch: number
  rtPress: number
  rtRelease: number
  pressDeadStroke: number
  releaseDeadStroke: number
  axis: number
  calibrate: number
}

export interface jingtaiV2SetPerformance {
  row: number
  col: number
  mode: number
  normalPress: number
  normalRelease: number
  rtFirstTouch: number
  rtPress: number
  rtRelease: number
  pressDeadStroke: number
  releaseDeadStroke: number
  axis: number
  calibrate: number
}

export interface IRouteInfo {
  route: number
  row: number
  data: number[]
}

export interface IADCSampleInfo {
  adc: number
  row: number
  data: number[]
}

export interface jingtaiV2CustomColor {
  R: number
  G: number
  B: number
  isCustom: boolean
}

export interface jingtaiV2ColorInput {
  R: number
  G: number
  B: number
}

// 0: 没有高级键
// 1: DKS（动态键程）
// 2: MPT（多点触发）
// 3: MT（模式切换/触发延迟）
// 4: TGL（触发延迟）
// 5: END（结束触发）
// 6: SOCD（同时按下冲突解决）
// 7: RS（保留）
export enum JingTaiV2SetMode {
  NONE = 0,
  DKS = 1,
  MPT = 2,
  MT = 3,
  TGL = 4,
  END = 5,
  SOCD = 6,
  RS = 7,
}

export const jingtaiV2MagnetModeConvert = (mode: JingTaiV2SetMode) => {
  let type = 0
  if (mode == JingTaiV2SetMode.MT) {
    type = MagnetKeyType.MT
  } else if (mode == JingTaiV2SetMode.DKS) {
    type = MagnetKeyType.DKS
  } else if (mode == JingTaiV2SetMode.TGL) {
    type = MagnetKeyType.TGL
  } else if (mode == JingTaiV2SetMode.SOCD) {
    type = MagnetKeyType.KME
  } else if (mode == JingTaiV2SetMode.MPT) {
    type = MagnetKeyType.MPT
  } else if (mode == JingTaiV2SetMode.END) {
    type = MagnetKeyType.END
  } else if (mode == JingTaiV2SetMode.RS) {
    type = MagnetKeyType.RS
  }
  return type
}

export interface jingtaiV2Macro {
  macroId: number
  offset: number
  actions: jingtaiV2MacroAction[]
}

export interface jingtaiV2MacroAction {
  status: number
  delay: number
  keyCode: number
}

export interface jingtaiV2MacroMode {
  macroId: number
  mode: number
  valid: boolean
  actNum: number
  repNum: number
}

export interface jingtaiV2ResHigherKey {
  row: number
  col: number
  mode: JingTaiV2SetMode
}

export interface jingtaiV2ResRate {
  key: string
  value: number
}
export interface jingtaiV2ResDefaultLayout {
  system: number
  fn: number
  row: number
  keyCodes: number[]
}
